import { iterate } from "@lib/iterate";
import { math } from "@lib/math";
import { isFiniteNum } from "@lib/number";
import { RenderingContext2D } from "./rendering-context-2d";

const tmpV2A=new math.Vec2();
const tmpV2B=new math.Vec2();
const tmpLine2=new math.Line2();

function styleToFontSize(
	defaultStyle:RenderingContext2D.TextStyle,
	style:RenderingContext2D.TextStyle,
):{size:number,unit:'m'|'px'}{
	let size=style.fontSize;
	let unit:'m'|'px'='px';
	if(typeof(size)==='string'){
		if(size.endsWith('%')){
			size=(+size.substring(0,size.length-1))*(+defaultStyle.fontSize)/100;
			unit='px';
		}else if(size.endsWith('m')){
			size=(+size.substring(0,size.length-1));
			unit='m';
		}else if(size.endsWith('px')){
			size=(+size.substring(0,size.length-2));
			unit='px';
		}
	}
	if(!isFiniteNum(size)){
		size=(+defaultStyle.fontSize);
		unit='px';
	}
	return {size,unit};
}

export function proccessTextItems(
	ctx:CanvasRenderingContext2D,
	pxWidth:number,
	pxUnitFactor:number,
	defaultStyle:RenderingContext2D.TextStyle,
	textTransformer=((text:string)=>[text]),
	items:RenderingContext2D.TextItem[],
	style:RenderingContext2D.TextStyle,
){
	// const pixelToCoord=coordToPixel.inverse();
	style={
		color: 'white',
		fontWeight: 'normal',
		fontFamily: 'Arial',
		...style
	};
	if(style.offset)
		style.offset=<[number,number]>style.offset.map(v=>v*pxUnitFactor);
	if(style.outline){
		style.outline={
			...style.outline,
			width: style.outline.width*pxUnitFactor,
		}
	}
	if(style.background){
		style.background={
			...style.background,
			padding: <[number,number,number,number]>style.background.padding.map(v=>v*pxUnitFactor),
		};
		if(style.background.outline){
			style.background.outline={
				...style.background.outline,
				width: style.background.outline.width*pxUnitFactor,
			};
		}
	}
	style.color=style.color.toString();
	if(style.outline)
		style.outline.color=style.outline.color.toString();
	if(style.background)
		style.background.color=style.background.color.toString();

	let fontSize=styleToFontSize(defaultStyle,style);
	if(fontSize.unit==='m'){
		fontSize.size/=pxWidth;
		fontSize.unit='px';
	}else{
		fontSize.size*=pxUnitFactor;
	}

	ctx.font=`${style.fontWeight} ${fontSize.size}px ${style.fontFamily}`;
	ctx.textBaseline='hanging';
	const lineHeight=fontSize.size*1.125;

	return items.map((item)=>{
		const out=proccessTextItem(ctx,lineHeight,textTransformer,item,style);
		if(item.anchor){
			if('x' in item.anchor){
				out.anchor=item.anchor.toXY();
			}else if(item.anchor instanceof math.LineString){
				out.anchor=item.anchor;
			}else if(item.anchor instanceof math.Line2){
				out.anchor=item.anchor;
			}else{
				const box=new math.Box2().expandByPoints(item.anchor);
				out.anchor=box.center();
			}
		}
		return out;
	});
}

function proccessTextItem(
	ctx:CanvasRenderingContext2D,
	lineHeight:number,
	textTransformer:((text:string)=>string[]),
	item:RenderingContext2D.TextItem,
	style:RenderingContext2D.TextStyle,
){
	let lines=item.text.split('\n');
	lines=lines.flatMap(textTransformer);
	const outerBox=new math.Box2();
	outerBox.min.set(0,0);
	outerBox.max.set(0,0);
	const widths:number[]=[];
	for(const [i,line] of lines.entries()){
		const metrics=ctx.measureText(line);
		outerBox.max.x=math.max(outerBox.max.x,metrics.width);
		const realLineHeight=math.max(metrics.actualBoundingBoxDescent,-(<any>metrics).alphabeticBaseline);
		outerBox.max.y=math.max(outerBox.max.y,i*lineHeight+realLineHeight);
		widths.push(metrics.width);
	}
	const innerBox=outerBox.clone();
	const {background}=style;
	if(background){
		outerBox.max.x+=background.padding[1]+background.padding[3];
		outerBox.max.y+=background.padding[0]+background.padding[2];
		innerBox.min.translate(background.padding[3],background.padding[0]);
		innerBox.max.translate(background.padding[3],background.padding[0]);
		if(background.outline){
			outerBox.max.x+=background.outline.width*2;
			outerBox.max.y+=background.outline.width*2;
			innerBox.min.translate(background.outline.width,background.outline.width);
			innerBox.max.translate(background.outline.width,background.outline.width);
		}
	}

	const _lines=lines.map((line,i)=>{
		let x=innerBox.min.x;
		if(style.horzAlign==='right')
			x=innerBox.max.x-widths[i];
		else if(style.horzAlign==='center')
			x=innerBox.center('x')-widths[i]/2;
		const y=innerBox.min.y+i*lineHeight;
		return {line,x,y};
	});

	return new ProcessedTextItem(
		item.text,
		style,
		_lines,
		outerBox,
		ctx.font,
	);	
}

export class ProcessedTextItem{
	public anchor:math.Vec2|math.Vec3|math.Line2|math.LineString<math.Vec2|math.Vec3>;

	public constructor(
		public readonly text:string,
		public readonly style:RenderingContext2D.TextStyle,
		public readonly lines:{line:string,x:number,y:number}[],
		public readonly outerBox:math.Box2,
		public readonly font:string,
	){
	}

	public pixelOrientation(
		coordToPixel:DOMMatrix,
		viewDownVecPx:math.Vec2,
	){
		const position=new math.Vec2();
		let rotation=0;
		const downVector=tmpV2A.copy(viewDownVecPx);
		const rightVector=tmpV2B.copy(downVector).rotate90().neg();

		if(this.anchor instanceof math.Vec2 || this.anchor instanceof math.Vec3){
			position.copy(coordToPixel.transformPoint(this.anchor));
		}else if(this.anchor instanceof math.Line2){
			const line=this.anchor;
			tmpLine2.a.copy(math.Vec2.from(coordToPixel.transformPoint(line.a)));
			tmpLine2.b.copy(math.Vec2.from(coordToPixel.transformPoint(line.b)));
			position.copy(tmpLine2.a).mean(tmpLine2.b);
			tmpLine2._dir(rightVector);
			downVector.copy(rightVector).rotate90();
		}else if(this.anchor){
			const ls=this.anchor.clone()
				.transform(p=>math.Vec2.from(coordToPixel.transformPoint(p)))
				.douglasPeuckerFilter((this.outerBox.size('y')));
			if(ls.count>1){
				const [a,b]=iterate(ls.pairs()).highest(([a,b])=>a.distToSq(b));
				tmpLine2.a.copy(a);
				tmpLine2.b.copy(b);
				position.copy(tmpLine2.a).mean(tmpLine2.b);
				tmpLine2._dir(rightVector);
				downVector.copy(rightVector).rotate90();
			}else if(ls.count>0){
				position.copy(ls.at(0));
			}
		}
		const {style,outerBox}=this;
		if(style.offset)
			position.addScaled(rightVector,style.offset[0] ?? 0).addScaled(downVector,style.offset[1] ?? 0);

		const offset=new math.Vec2(0,0);
		if(style.vertAlign==='middle')
			offset.y-=outerBox.size('y')/2;
		else if(style.vertAlign==='bottom')
			offset.y-=outerBox.size('y');
		if(style.horzAlign==='center')
			offset.x-=outerBox.size('x')/2;
		else if(style.horzAlign==='right')
			offset.x-=outerBox.size('x');
	
		//const offset=calcOffset(this.style,this.outerBox,rightVector,downVector);
		if(downVector.dot(viewDownVecPx)<0){
			downVector.neg();
		}
		rotation=Math.atan2(-downVector.x,downVector.y);
		return <const>[position,offset,rotation];
	}

	public draw(ctx:CanvasRenderingContext2D){
		const {style,outerBox,lines,font}=this;
		const {outline,background}=style;
		ctx.font=font;
		ctx.lineCap='round';
		ctx.setLineDash([]);
		if(background){
			ctx.lineJoin='miter';
			ctx.fillStyle=background.color.toString();
			ctx.beginPath();
			ctx.rect(outerBox.min.x,outerBox.min.y,outerBox.size('x'),outerBox.size('y'));
			if(background.outline){
				ctx.strokeStyle=background.outline.color;
				ctx.lineWidth=background.outline.width*2;
				ctx.stroke();
			}
			ctx.fill();
		}

		ctx.lineJoin='round';
		ctx.fillStyle=style.color.toString();
		if(outline){
			ctx.strokeStyle=outline.color.toString();
			ctx.lineWidth=outline.width*2;
		}
		for(const {x,y,line} of lines){
			if(outline)
				ctx.strokeText(line,x,y);
			ctx.fillText(line,x,y);
		}
	}
}
